// src/components/i18n/LanguagePickerSheet.tsx
//
// Reusable picker UI: cloud of prominent languages on top, A-Z
// list below. Used by:
//   - LanguageGateScreen (phase 2 of the first-launch flow)
//   - LanguageFlagButton in the topbar (mid-session change)
//
// The component is purely presentational + selection-callback;
// persistence and standby-screen flow live in the consumer
// (LanguageGate / the future settings sheet).

import React, { useMemo } from 'react';
import { Box, Stack, Typography, ButtonBase } from '@mui/material';
import {
  ALPHABETICAL_LANGUAGES,
  PROMINENT_LANGUAGES,
  LanguageEntry,
} from '../../i18n/languages';
import { useT } from '../../i18n/useT';

interface LanguagePickerSheetProps {
  /** Optional logo node displayed above the title. */
  header?: React.ReactNode;
  /** Title shown above the cloud. Defaults to "Choose your language". */
  title?: string;
  /** Subtitle (small text under the title). */
  subtitle?: string;
  /** Currently-selected language, highlighted in both cloud + list. */
  current?: LanguageEntry | null;
  /** Called with the selected language entry. Caller persists. */
  onSelect: (entry: LanguageEntry) => void;
}

export const LanguagePickerSheet: React.FC<LanguagePickerSheetProps> = ({
  header,
  title = 'Choose your language',
  subtitle = "We'll use this for everything in the app — guide content, chat, and discovery.",
  current,
  onSelect,
}) => {
  const t = useT();
  const cloudPills = useMemo(
    () =>
      PROMINENT_LANGUAGES.map((lang, i) => {
        const tier: 0 | 1 | 2 = i < 4 ? 0 : i < 6 ? 1 : 2;
        return { lang, tier };
      }),
    []
  );

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: { xs: 5, sm: 7 },
        pb: 6,
        px: { xs: 3, sm: 4 },
      }}
    >
      {(header || title) && (
        <Stack alignItems="center" spacing={2.5} sx={{ mb: 5 }}>
          {header}
          {title && (
            <Typography
              sx={{
                fontFamily: '"Fraunces", "Iowan Old Style", "Palatino", Georgia, serif',
                fontWeight: 500,
                fontSize: { xs: 28, sm: 36 },
                lineHeight: 1.15,
                letterSpacing: '-0.015em',
                color: '#1A1A1A',
                textAlign: 'center',
              }}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography
              sx={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 14,
                color: 'rgba(26, 26, 26, 0.55)',
                textAlign: 'center',
                maxWidth: 360,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Stack>
      )}

      {/* Cloud of prominent languages */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 1.25, sm: 1.5 },
          justifyContent: 'center',
          alignItems: 'center',
          maxWidth: 720,
          mx: 'auto',
          mb: 6,
        }}
      >
        {cloudPills.map(({ lang, tier }, i) => (
          <CloudPill
            key={lang.code}
            lang={lang}
            tier={tier}
            staggerIndex={i}
            selected={current?.code === lang.code}
            onClick={() => onSelect(lang)}
          />
        ))}
      </Box>

      {/* Hairline divider */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          maxWidth: 480,
          width: '100%',
          mx: 'auto',
          mb: 2.5,
        }}
      >
        <Box sx={{ flex: 1, height: 1, bgcolor: 'rgba(26,26,26,0.08)' }} />
        <Typography
          sx={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 11,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: 'rgba(26, 26, 26, 0.45)',
          }}
        >
          
          {t("all languages")}
        </Typography>
        <Box sx={{ flex: 1, height: 1, bgcolor: 'rgba(26,26,26,0.08)' }} />
      </Box>

      <Box sx={{ width: '100%', maxWidth: 480, mx: 'auto' }}>
        {ALPHABETICAL_LANGUAGES.map((lang) => (
          <ListRow
            key={lang.code}
            lang={lang}
            selected={current?.code === lang.code}
            onClick={() => onSelect(lang)}
          />
        ))}
      </Box>
    </Box>
  );
};

const CloudPill: React.FC<{
  lang: LanguageEntry;
  tier: 0 | 1 | 2;
  staggerIndex: number;
  selected: boolean;
  onClick: () => void;
}> = ({ lang, tier, staggerIndex, selected, onClick }) => {
  const tierStyles = [
    { fontSize: 22, py: 1.25, px: 2.5, weight: 600 },
    { fontSize: 17, py: 1, px: 2, weight: 500 },
    { fontSize: 14, py: 0.75, px: 1.5, weight: 500 },
  ][tier];

  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`Use ${lang.englishName}`}
      sx={{
        borderRadius: 999,
        border: selected
          ? '1px solid #0F4C75'
          : '1px solid rgba(15,76,117,0.18)',
        bgcolor: selected ? '#0F4C75' : '#FFFFFF',
        color: selected ? '#FFFFFF' : '#0F4C75',
        py: tierStyles.py,
        px: tierStyles.px,
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: tierStyles.fontSize,
        fontWeight: tierStyles.weight,
        lineHeight: 1.1,
        boxShadow: selected
          ? '0 4px 14px rgba(15,76,117,0.18)'
          : '0 1px 2px rgba(15,76,117,0.04)',
        transition:
          'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease, color 180ms ease',
        animation: 'pickerPillEnter 360ms ease-out both',
        animationDelay: `${staggerIndex * 35}ms`,
        '@keyframes pickerPillEnter': {
          '0%': { opacity: 0, transform: 'translateY(6px) scale(0.96)' },
          '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
        },
        '&:hover, &:focus-visible': {
          bgcolor: selected ? '#093553' : '#0F4C75',
          color: '#FFFFFF',
          boxShadow: '0 4px 14px rgba(15,76,117,0.18)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <span dir={lang.rtl ? 'rtl' : 'ltr'}>{lang.nativeName}</span>
    </ButtonBase>
  );
};

const ListRow: React.FC<{
  lang: LanguageEntry;
  selected: boolean;
  onClick: () => void;
}> = ({ lang, selected, onClick }) => {
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`Use ${lang.englishName}`}
      aria-current={selected ? 'true' : undefined}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        textAlign: 'left',
        py: 1.5,
        px: 1,
        borderBottom: '1px solid rgba(26,26,26,0.06)',
        transition: 'background-color 140ms ease',
        bgcolor: selected ? 'rgba(15,76,117,0.06)' : 'transparent',
        '&:hover, &:focus-visible': {
          bgcolor: 'rgba(15,76,117,0.05)',
        },
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 16,
          fontWeight: selected ? 600 : 500,
          color: selected ? '#0F4C75' : '#1A1A1A',
        }}
        dir={lang.rtl ? 'rtl' : 'ltr'}
      >
        {lang.nativeName}
      </Typography>
      <Typography
        sx={{
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 13,
          color: 'rgba(26,26,26,0.45)',
          ml: 2,
          flexShrink: 0,
        }}
      >
        {lang.englishName}
      </Typography>
    </ButtonBase>
  );
};
